import { useEffect, useState } from 'react';
import { Loader2, Mail, Plus, Search, Pencil, UserSearch } from 'lucide-react';
import { FindContacts } from '@generated/components/FindContacts';
import { AgencyPeople } from '@generated/components/AgencyPeople';
import { SendBadges } from '@generated/components/SendBadges';
import { BulkFind } from '@generated/components/BulkFind';
import { getAgencies, getAgencyCounts, saveAgency } from '@generated/server/agencies';
import { getLetters, sendLetterToAgency } from '@generated/server/letters';
import { LettersPanel, type LettersData } from '@generated/components/LettersPanel';
import { getStaffPass } from '@generated/hooks/staffPass';
import { GrowList } from '@generated/components/GrowList';
import { AgencyDialogs, type Agency, type Status, STATUSES } from '@generated/components/AgencyDialogs';

export function Agencies() {
  const [items, setItems] = useState<Agency[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [more, setMore] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [status, setStatus] = useState<Status | ''>('');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [tick, setTick] = useState(0);
  const [edit, setEdit] = useState<Agency | 'new' | null>(null);
  const [pitch, setPitch] = useState<Agency | null>(null);
  const [notice, setNotice] = useState('');
  const [finding, setFinding] = useState<Agency | null>(null);
  const [letters, setLetters] = useState<LettersData | null>(null);
  const [lt, setLt] = useState(0);
  useEffect(() => { getLetters({ data: { pass: getStaffPass() } }).then((r) => setLetters(r ?? null)).catch((e) => { console.error(e); setError('Could not load letters.'); }); }, [lt]);

  useEffect(() => { const t = setTimeout(() => setDq(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    let on = true;
    setRefetching(true); setError('');
    getAgencies({ data: { pass: getStaffPass(), q: dq || undefined, status: status || undefined } })
      .then((r) => { if (on) { setItems(r?.items ?? []); setCursor(r?.cursor ?? null); } })
      .catch((e) => { console.error(e); if (on) setError('Could not load agencies.'); })
      .finally(() => { if (on) { setLoading(false); setRefetching(false); } });
    getAgencyCounts({ data: { pass: getStaffPass() } })
      .then((rows) => on && setCounts(Object.fromEntries((rows ?? []).map((r) => [r.outreachStatus ?? '', r.n]))))
      .catch(console.error);
    return () => { on = false; };
  }, [dq, status, tick]);

  async function loadMore() {
    if (!cursor) return;
    setMore(true);
    try { const r = await getAgencies({ data: { pass: getStaffPass(), cursor } }); setItems((p) => [...p, ...(r?.items ?? [])]); setCursor(r?.cursor ?? null); }
    catch (e) { console.error(e); setError('Could not load more.'); } finally { setMore(false); }
  }

  async function onSave(v: Parameters<typeof saveAgency>[0]['data']) {
    await saveAgency({ data: { ...v, pass: getStaffPass() } });
    setEdit(null); setNotice(`Saved ${v.name}.`); setTick((t) => t + 1);
  }
  async function onSend(a: Agency, letterNumber: number, to: string, subject: string, body: string) {
    await sendLetterToAgency({ data: { pass: getStaffPass(), agencyId: a.id, letterNumber, to, subject, body } });
    setPitch(null); setNotice(`Letter ${letterNumber} sent to ${a.name}.`); setTick((t) => t + 1); setLt((x) => x + 1);
  }

  return (
    <section className="space-y-6">
      <header className="border-b-4 border-double border-foreground pb-4">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Outreach desk</p>
        <h1 className="font-[family-name:var(--font-heading)] text-4xl font-bold">Ad Agencies</h1>
        <p className="mt-1 text-sm text-muted-foreground">Pitch the portal and media kit to media buyers. Sending marks them "Media Kit Sent" with today's date.</p>
      </header>
      <LettersPanel data={letters} onChanged={() => { setLt((x) => x + 1); setTick((x) => x + 1); }} onNotice={setNotice} />
      <BulkFind onDone={(m) => { setNotice(m); setTick((x) => x + 1); setLt((x) => x + 1); }} />
      <GrowList onAdded={(msg) => { setNotice(msg); setTick((t) => t + 1); }} />
      <div className="flex flex-wrap gap-2">
        {(['', ...STATUSES] as const).map((s) => (
          <button key={s || 'all'} onClick={() => setStatus(s)} className={`min-h-10 border-2 border-foreground px-3 text-xs font-semibold uppercase ${status === s ? 'bg-foreground text-background' : 'bg-card hover:bg-muted'}`}>
            {s || 'All'}{s && counts[s] ? ` · ${counts[s]}` : ''}
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <label className="flex min-h-11 flex-1 items-center gap-2 border-2 border-foreground bg-card px-3">
          <Search className="h-4 w-4" aria-hidden /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search agencies" className="w-full bg-transparent outline-none" />
          {refetching && <Loader2 className="h-4 w-4 animate-spin" />}
        </label>
        <button onClick={() => setEdit('new')} className="inline-flex min-h-11 items-center justify-center gap-2 border-2 border-foreground bg-primary px-4 font-semibold text-primary-foreground"><Plus className="h-4 w-4" />Add agency</button>
      </div>
      {notice && <p className="border-l-4 border-primary bg-muted px-3 py-2 text-sm">{notice}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading ? <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse bg-muted" />)}</div> : (
        <ul className={`divide-y-2 divide-foreground/15 border-2 border-foreground bg-card ${refetching ? 'opacity-60' : ''}`}>
          {items.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground">No agencies match.</li>}
          {items.map((a) => (
            <li key={a.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-bold">{a.name}</h3>
                  <span className="border border-foreground px-2 text-[11px] font-semibold uppercase">{a.outreachStatus ?? 'Not Contacted'}</span>
                </div>
                <p className="text-xs text-muted-foreground">{[a.focus, a.borougharea, a.contactName].filter(Boolean).join(' · ')}</p>
                <p className="mt-1 break-words text-sm">
                  {a.email?.email && <><a className="underline" href={`mailto:${a.email.email}`}>{a.email.email}</a> <SendBadges sends={letters?.sends ?? []} email={a.email.email} /></>}
                  {a.phone?.phone && <span className="ml-3">{a.phone.phone}</span>}
                  {a.website?.url && <a className="ml-3 underline" href={a.website.url} target="_blank" rel="noreferrer">{a.website.label ?? 'website'}</a>}
                </p>
                {a.address ? <p className="mt-1 whitespace-pre-line text-sm">{a.address}</p> : <p className="mt-1 text-xs italic text-muted-foreground">No mailing address yet</p>}
                {a.notes && <p className="mt-1 text-sm text-muted-foreground">{a.notes}</p>}
                <AgencyPeople agency={a} data={letters} onChanged={() => { setLt((x) => x + 1); setTick((x) => x + 1); }} onNotice={setNotice} />
                {(() => {
                  const ok = (letters?.sends ?? []).filter((s) => s.agencyId === a.id && s.status !== 'failed');
                  const people = (a.email?.email ? 1 : 0) + (letters?.contacts ?? []).filter((c) => c.agencyId === a.id).length;
                  const nums = [...new Set(ok.map((s) => s.letterNumber))].sort((x, y) => x - y);
                  if (!nums.length) return <p className="mt-1 text-xs font-semibold">No letters sent yet</p>;
                  return <p className="mt-1 text-xs font-semibold">{nums.map((n) => `Letter ${n} received by ${new Set(ok.filter((s) => s.letterNumber === n).map((s) => s.to.toLowerCase())).size} of ${people}`).join(' · ')}</p>;
                })()}
                {a.lastContacted && <p className="mt-1 text-xs">Last contacted {a.lastContacted.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })}</p>}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <button onClick={() => setEdit(a)} aria-label={`Edit ${a.name}`} className="inline-flex min-h-11 min-w-11 items-center justify-center border-2 border-foreground hover:bg-muted"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setFinding(a)} className="inline-flex min-h-11 items-center gap-2 border-2 border-foreground bg-card px-3 text-sm font-semibold hover:bg-muted"><UserSearch className="h-4 w-4" />Find contacts</button>
                <button onClick={() => setPitch(a)} className="inline-flex min-h-11 items-center gap-2 border-2 border-foreground bg-[hsl(var(--brand-yellow))] px-3 text-sm font-semibold"><Mail className="h-4 w-4" />Send a letter</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {cursor && <button onClick={loadMore} disabled={more} className="mx-auto flex min-h-11 items-center gap-2 border-2 border-foreground px-5 font-semibold">{more && <Loader2 className="h-4 w-4 animate-spin" />}Load more</button>}
      <FindContacts agency={finding} onClose={() => { setFinding(null); setTick((x) => x + 1); setLt((x) => x + 1); }} onSaved={setNotice} />
      <AgencyDialogs edit={edit} pitch={pitch} onCloseEdit={() => setEdit(null)} onClosePitch={() => setPitch(null)} letters={letters?.letters ?? []} people={letters?.contacts ?? []} sends={letters?.sends ?? []} onPeopleChanged={() => setLt((x) => x + 1)} onSave={onSave} onSend={onSend} />
    </section>
  );
}
